import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';

const WAVE = "url('https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=1800&q=85&auto=format&fit=crop')";
const GLASS = { background:'rgba(4,16,35,0.72)', backdropFilter:'blur(32px) saturate(180%)', WebkitBackdropFilter:'blur(32px) saturate(180%)', border:'1px solid rgba(255,255,255,0.13)', boxShadow:'0 24px 64px rgba(0,0,0,0.4)' };

function Input({ label, type='text', value, onChange, placeholder='', hint='', error='' }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom:'1.25rem' }}>
      <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'rgba(255,255,255,0.5)', marginBottom:'0.5rem' }}>{label}</label>
      <input
        type={type} value={value} onChange={e=>onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}
        style={{ width:'100%', padding:'0.875rem 1rem', background:'rgba(255,255,255,0.07)', border:`1.5px solid ${error?'rgba(239,68,68,0.6)':focused?'rgba(3,139,148,0.7)':'rgba(255,255,255,0.15)'}`, borderRadius:12, color:'white', fontSize:'0.9rem', outline:'none', fontFamily:'DM Sans,sans-serif', boxSizing:'border-box', transition:'border-color 0.2s', letterSpacing: type==='password'?'0.1em':'normal' }}
      />
      {hint && !error && <p style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.35)', marginTop:'0.35rem' }}>{hint}</p>}
      {error && <p style={{ fontSize:'0.68rem', color:'#fca5a5', marginTop:'0.35rem' }}>{error}</p>}
    </div>
  );
}

function StrengthBar({ password }) {
  if (!password) return null;
  const checks = [password.length>=8, /[A-Z]/.test(password), /[0-9]/.test(password), /[^A-Za-z0-9]/.test(password)];
  const score = checks.filter(Boolean).length;
  const colors = ['#ef4444','#f97316','#eab308','#22c55e'];
  const labels = ['Weak','Fair','Good','Strong'];
  return (
    <div style={{ marginBottom:'1.25rem' }}>
      <div style={{ display:'flex', gap:'4px', marginBottom:'0.35rem' }}>
        {[0,1,2,3].map(i=>(
          <div key={i} style={{ flex:1, height:3, borderRadius:99, background: i<score ? colors[score-1] : 'rgba(255,255,255,0.1)', transition:'background 0.3s' }}/>
        ))}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <p style={{ fontSize:'0.65rem', color: score>0 ? colors[score-1] : 'rgba(255,255,255,0.3)', fontWeight:700 }}>{score>0?labels[score-1]:'Enter password'}</p>
        <div style={{ display:'flex', gap:'0.75rem' }}>
          {[['8+ chars',password.length>=8],['A-Z',/[A-Z]/.test(password)],['0-9',/[0-9]/.test(password)],['#!@',/[^A-Za-z0-9]/.test(password)]].map(([l,p])=>(
            <span key={l} style={{ fontSize:'0.6rem', color:p?'#4ade80':'rgba(255,255,255,0.25)', fontWeight:700, transition:'color 0.2s' }}>{p?'✓ ':''}{l}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'done'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSendCode = async () => {
    if (!email || !email.includes('@')) { setError('Please enter a valid email address.'); return; }
    setLoading(true); setError('');
    try {
      await resetPassword({ username: email });
      setStep('code');
    } catch (e) {
      // Show generic message to avoid user enumeration
      setStep('code');
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    const errs = {};
    if (!code || code.length < 4) errs.code = 'Enter the 6-digit code from your email.';
    if (!newPw || newPw.length < 8) errs.newPw = 'Password must be at least 8 characters.';
    if (newPw !== confirmPw) errs.confirmPw = 'Passwords do not match.';
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setLoading(true); setError(''); setFieldErrors({});
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword: newPw });
      setStep('done');
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('CodeMismatch') || msg.includes('Invalid code')) setFieldErrors({ code:'Invalid code. Check your email and try again.' });
      else if (msg.includes('ExpiredCode') || msg.includes('expired')) setFieldErrors({ code:'This code has expired. Go back and request a new one.' });
      else if (msg.includes('Password')) setFieldErrors({ newPw: msg });
      else setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:'100vh', backgroundImage:WAVE, backgroundSize:'cover', backgroundPosition:'center', display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
      <div style={{ position:'fixed', inset:0, background:'rgba(2,8,20,0.68)', pointerEvents:'none' }}/>

      <div style={{ position:'relative', zIndex:1, width:'100%', maxWidth:440, animation:'fadeUp 0.4s ease' }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:'2rem' }}>
          <h1 style={{ fontFamily:'Playfair Display,serif', fontSize:'2.2rem', fontWeight:800, color:'white', letterSpacing:'-0.5px', textShadow:'0 2px 16px rgba(0,0,0,0.3)', margin:0 }}>
            Vyb<span style={{ color:'rgba(255,255,255,0.5)' }}>lo</span>
          </h1>
        </div>

        <div style={{ ...GLASS, borderRadius:24, overflow:'hidden' }}>
          {/* Header gradient bar */}
          <div style={{ background:'linear-gradient(135deg, rgba(3,54,111,0.9) 0%, rgba(3,139,148,0.85) 100%)', padding:'1.75rem 2rem' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
              <div style={{ width:40, height:40, borderRadius:12, background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem', flexShrink:0 }}>
                {step==='done' ? '✓' : '🔑'}
              </div>
              <div>
                <h2 style={{ fontFamily:'Playfair Display,serif', fontSize:'1.15rem', fontWeight:800, color:'white', margin:'0 0 2px' }}>
                  {step==='email' && 'Reset your password'}
                  {step==='code' && 'Check your email'}
                  {step==='done' && 'Password updated!'}
                </h2>
                <p style={{ fontSize:'0.75rem', color:'rgba(255,255,255,0.6)', margin:0 }}>
                  {step==='email' && "We'll send a reset code to your email"}
                  {step==='code' && `Code sent to ${email}`}
                  {step==='done' && 'You can now sign in with your new password'}
                </p>
              </div>
            </div>
          </div>

          <div style={{ padding:'2rem' }}>

            {/* Step 1: Email */}
            {step==='email' && (
              <>
                <Input label="Email address" type="email" value={email} onChange={v=>{setEmail(v);setError('');}} placeholder="you@example.com"/>
                {error && <p style={{ fontSize:'0.8rem', color:'#fca5a5', marginBottom:'1rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:8, padding:'0.625rem 0.875rem' }}>{error}</p>}
                <button onClick={handleSendCode} disabled={loading}
                  style={{ width:'100%', padding:'0.9rem', background:'linear-gradient(135deg,#03366f,#038b94)', border:'none', borderRadius:12, color:'white', fontWeight:700, fontSize:'0.925rem', cursor:loading?'not-allowed':'pointer', fontFamily:'DM Sans,sans-serif', opacity:loading?0.7:1, marginBottom:'1rem', transition:'opacity 0.2s' }}>
                  {loading ? 'Sending code…' : 'Send reset code →'}
                </button>
                <button onClick={()=>navigate('/login')}
                  style={{ width:'100%', padding:'0.7rem', background:'transparent', border:'1px solid rgba(255,255,255,0.15)', borderRadius:12, color:'rgba(255,255,255,0.55)', fontWeight:600, fontSize:'0.85rem', cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                  ← Back to sign in
                </button>
              </>
            )}

            {/* Step 2: Code + new password */}
            {step==='code' && (
              <>
                <div style={{ background:'rgba(3,139,148,0.12)', border:'1px solid rgba(3,139,148,0.25)', borderRadius:10, padding:'0.875rem 1rem', marginBottom:'1.5rem', fontSize:'0.78rem', color:'rgba(255,255,255,0.7)', lineHeight:1.6 }}>
                  📬 We sent a 6-digit code to <strong style={{ color:'white' }}>{email}</strong>. Check your spam folder if you don't see it.
                </div>

                {/* Big code input */}
                <div style={{ marginBottom:'1.25rem' }}>
                  <label style={{ display:'block', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'rgba(255,255,255,0.5)', marginBottom:'0.5rem' }}>Verification Code</label>
                  <input
                    type="text" inputMode="numeric" maxLength={6} value={code} onChange={e=>{ setCode(e.target.value.replace(/\D/g,'')); setFieldErrors(p=>({...p,code:''})); }}
                    placeholder="000000"
                    style={{ width:'100%', padding:'1rem', background:'rgba(255,255,255,0.07)', border:`1.5px solid ${fieldErrors.code?'rgba(239,68,68,0.6)':'rgba(255,255,255,0.15)'}`, borderRadius:12, color:'white', fontSize:'1.8rem', fontWeight:700, outline:'none', fontFamily:'monospace', letterSpacing:'0.5em', textAlign:'center', boxSizing:'border-box', transition:'border-color 0.2s' }}
                    onFocus={e=>{ if(!fieldErrors.code) e.target.style.borderColor='rgba(3,139,148,0.7)'; }}
                    onBlur={e=>{ if(!fieldErrors.code) e.target.style.borderColor='rgba(255,255,255,0.15)'; }}
                  />
                  {fieldErrors.code && <p style={{ fontSize:'0.68rem', color:'#fca5a5', marginTop:'0.35rem' }}>{fieldErrors.code}</p>}
                </div>

                <Input label="New Password" type="password" value={newPw} onChange={v=>{setNewPw(v);setFieldErrors(p=>({...p,newPw:''}));}} placeholder="Min 8 characters" error={fieldErrors.newPw||''}/>
                <StrengthBar password={newPw}/>
                <Input label="Confirm New Password" type="password" value={confirmPw} onChange={v=>{setConfirmPw(v);setFieldErrors(p=>({...p,confirmPw:''}));}} placeholder="Same password again" error={fieldErrors.confirmPw||''}/>

                {error && <p style={{ fontSize:'0.8rem', color:'#fca5a5', marginBottom:'1rem', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:8, padding:'0.625rem 0.875rem' }}>{error}</p>}

                <button onClick={handleResetPassword} disabled={loading || code.length<4 || !newPw || !confirmPw}
                  style={{ width:'100%', padding:'0.9rem', background: (code.length>=4&&newPw&&confirmPw) ? 'linear-gradient(135deg,#03366f,#038b94)' : 'rgba(255,255,255,0.08)', border:'none', borderRadius:12, color:'white', fontWeight:700, fontSize:'0.925rem', cursor:loading||code.length<4||!newPw||!confirmPw?'not-allowed':'pointer', fontFamily:'DM Sans,sans-serif', opacity:loading?0.7:1, marginBottom:'1rem', transition:'all 0.2s' }}>
                  {loading ? 'Updating password…' : 'Update password →'}
                </button>

                <div style={{ display:'flex', gap:'0.75rem' }}>
                  <button onClick={()=>setStep('email')}
                    style={{ flex:1, padding:'0.65rem', background:'transparent', border:'1px solid rgba(255,255,255,0.15)', borderRadius:10, color:'rgba(255,255,255,0.5)', fontWeight:600, fontSize:'0.8rem', cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                    ← Change email
                  </button>
                  <button onClick={handleSendCode}
                    style={{ flex:1, padding:'0.65rem', background:'transparent', border:'1px solid rgba(255,255,255,0.15)', borderRadius:10, color:'rgba(255,255,255,0.5)', fontWeight:600, fontSize:'0.8rem', cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                    Resend code
                  </button>
                </div>
              </>
            )}

            {/* Step 3: Done */}
            {step==='done' && (
              <div style={{ textAlign:'center', padding:'1rem 0' }}>
                <div style={{ width:72, height:72, borderRadius:'50%', background:'rgba(34,197,94,0.15)', border:'2px solid rgba(34,197,94,0.4)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2rem', margin:'0 auto 1.25rem' }}>✓</div>
                <p style={{ color:'rgba(255,255,255,0.65)', fontSize:'0.875rem', lineHeight:1.6, marginBottom:'1.75rem' }}>
                  Your password has been updated. You can now sign in with your new password.
                </p>
                <button onClick={()=>navigate('/login')}
                  style={{ width:'100%', padding:'0.9rem', background:'linear-gradient(135deg,#03366f,#038b94)', border:'none', borderRadius:12, color:'white', fontWeight:700, fontSize:'0.925rem', cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                  Sign in →
                </button>
              </div>
            )}
          </div>
        </div>

        <p style={{ textAlign:'center', color:'rgba(255,255,255,0.25)', fontSize:'0.7rem', marginTop:'1.5rem' }}>
          © 2025 Vyblo. All rights reserved.
        </p>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        input::placeholder { color: rgba(255,255,255,0.25); }
        input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px rgba(4,16,35,0.9) inset !important; -webkit-text-fill-color: white !important; }
      `}</style>
    </div>
  );
}
